import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';

import { Observable, from, of } from 'rxjs';
import { catchError, retry, switchMap, tap } from 'rxjs/operators';

import { environment } from '../environments/environments';
import { base64ToBytes, createObfuscatedPayload, importAesKey } from './web-crypto.helper';

type ClientData = {
  fingerprint: string;
  timestamp: number;
  timezone: string;
  nonce: string;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly #http = inject(HttpClient);
  readonly #router = inject(Router);

  readonly #keyB64 = environment.CRYPTO_AES_KEY;
  readonly #ivB64 = environment.CRYPTO_AES_IV;

  readonly #keyPromise = importAesKey(this.#keyB64);
  readonly #iv = base64ToBytes(this.#ivB64);

  secureLogin(): Observable<null | void> {
    const loginData: ClientData = {
      fingerprint: this.generateClientFingerprint(),
      timestamp: Date.now(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      nonce: this.generateNonce(),
    };

    return this.loginWithoutToken(loginData);
  }

  private loginWithoutToken(loginData: ClientData): Observable<null | void> {
    // We save the route different tha the fallback one to redirect after success in case the user reload from the fallback route
    const currentPath = window.location.href.slice(window.location.origin.length);
    if (currentPath !== 'auth-fallback') sessionStorage.setItem('redirectTo', currentPath);

    return from(createObfuscatedPayload(loginData, this.#keyPromise, this.#iv)).pipe(
      switchMap((payload) => this.cookieRequest(payload)),
      catchError((error) => this.handleRequestError(error))
    );
  }

  cookieRequest(payload: string): Observable<void> {
    return this.#http
      .post<void>(`${environment.CASHBACK_API}Login`, null, {
        headers: new HttpHeaders({
          'X-Client-Data': payload,
        }),
      })
      .pipe(
        retry({ count: 3, delay: 500 }),
        tap(() => this.#router.navigateByUrl(sessionStorage.getItem('redirectTo') || ''))
      );
  }

  private handleRequestError(error: HttpErrorResponse): Observable<null> {
    console.error(`Authentication failed`, error);
    this.#router.navigateByUrl('auth-fallback');
    return of(null);
  }

  private generateClientFingerprint(): string {
    const components = [
      navigator.userAgent,
      navigator.language,
      `${screen.width}x${screen.height}`,
      new Date().getTimezoneOffset().toString(),
      navigator.platform,
    ];

    let hash = 0;
    const combined = components.join('|');
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private generateNonce(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // hex string
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}
